import { SFX } from '../platform/audio';
import { groundYAt } from '../world/arena';
import { P } from '../world/player';
import { GRANT } from './meta-parts';
import { STATE, areaDamage, shake, spawnRing } from './fx';
/* ============================================================
   推進器飛行 —— 貼起來、燃料耗完就落地
   ============================================================

   規格（使用者逐題拍板）：`.claude/notes/flight-spec.md`。
   一句話版本：**Shift 長按上升，放開快速下墜，落地砸出衝擊波。**

   三個「只有這樣做才成立」的地方：

   1. **推進能量與衝刺充能各自獨立。** 共用一池在紙上更有取捨，實際上
      閃避是生存必需，玩家會永遠不敢把它花在飛行上 —— 那個系統就形同虛設
      （同型的坑記在 lessons：機制沒接上時，症狀是「這功能還在嗎」）。
   2. **落地判定問 `groundYAt(x,z)`，不寫死 0。** 地圖那一輪要加可站立平台，
      到時候只改那一個函式，玩家與敵人都不用動。
   3. **空中的代價不是數值懲罰，是失去掩體。** 子彈與視線本來就被地形擋
      （`pointBlocked`/`losBlocked`），一離地那層保護自然消失 ——
      看不見的扣血只會讓人覺得「飛起來就死得快」而不知道為什麼。

   決定性：這個檔跑在**模擬步**上，完全不碰亂數，
   狀態全部掛在 `P` 上（`snapshot()` 讀得到，換場由 `equipMech` 歸零）。 */

export var FLY = {
  /** 絕對天花板。**這不是設計上的高度限制**（那件事由下面的耗能曲線做），
      只是一條安全網，免得任何倍率意外讓機體飛出鏡頭。 */
  maxH: 20,
  /** 高度懲罰的基準（使用者 2026-08-03：「飛太高能量條就直線下降得很快」）。
      耗能 ＝ `use × (1 + (高度 / softH)²)` —— 5.5 高度時 ×2、11 時 ×5、
      14 時 ×7.5。玩家因此**自己停在 8–11 那一帶**，而不是撞到一堵看不見的天花板：
      硬上限會讓人以為卡住了，耗能曲線則是「我知道我在燒錢」。 */
  softH: 5.5,
  /** 貼地滯空的能量消耗（每秒）。重裝 120 點 ≈ 3.5 秒，輕型 90 點 ≈ 2.6 秒；
      飛高之後這個數會被上面的曲線放大。 */
  use: 34,
  /** **衝刺的能量成本**（佔上限的比例，2026-08-06 使用者指定「衝刺與飛行共用一條」）。
      25% ＝ 滿能量剛好衝四次，而且跟起飛門檻（40%）湊出一個真的決策：
      衝兩次還飛得起來（50%），衝三次就飛不了（25%）——
      「要留著跳，還是現在閃」變成每一次衝刺都要回答的問題。
      在這之前衝刺是獨立的格數＋冷卻，跟飛行毫無交互，兩條資源各自為政。 */
  dashPct: 0.25,
  /** 起飛門檻＝能量上限的 **40%**（2026-08-03 訂 65%，2026-08-06 使用者下修到 40%）。

      下修的理由是這條能量在 08-06 之後**同時餵衝刺**：門檻 65% 時，
      衝一次（−25%）就跌到 75%→仍可飛，衝兩次直接飛不了 ——
      而衝刺是閃避，玩家不該為了保留起飛權而不敢閃。40% 之後衝兩次還飛得起來。

      這是整個系統的難度閥門。門檻低的話玩法會塌成「落地→起飛→落地→起飛」，
      地面怪從此毫無威脅 —— 飛行必須是**偶爾用一次的爆發**，不是走路的替代品。
      ⚠️ 續飛只要還有一滴能量（見 `canFly`）：兩個條件不同是刻意的，
      否則能量掉到 65% 以下的那一刻會在空中突然斷推進。 */
  minTakeoffPct: 0.40,
  /** 停用多久才開始回充。要有這個延遲，「連續點放」才不會比持續飛更划算。 */
  regenDelay: 1.2,
  /** 站在地上回得比較快：要飛就得先落回地面站一段時間。 */
  groundRegenMul: 1.5,
  fallAcc: 38, fallMax: 34,
  /** 從這個高度以上落下才砸得出衝擊波（小跳不算）。 */
  slamMinH: 3,
  slamR: 5.5,
  /** 空中的水平手感：稍快但轉向遲鈍（降落點要預判，才有操作感）。 */
  airSpdMul: 1.15, airTurn: 5, groundTurn: 11
};

/** 現在起飛需要多少能量（上限的 40%）。HUD 與模擬用同一個來源，
    否則條的顏色與「按了有沒有反應」會對不上。 */
export function takeoffCost() {
  return (P.s.thrustMax || 100) * FLY.minTakeoffPct;
}

/** 一次衝刺要多少能量。局外部件可以減免（`GRANT.dashCut`，0–1）。
    跟 `takeoffCost` 同一個道理：HUD 判斷「這一下按得動嗎」必須用同一個來源。 */
export function dashCost() {
  var cut = Math.max(0, Math.min(0.6, GRANT.dashCut || 0));
  return (P.s.thrustMax || 100) * FLY.dashPct * (1 - cut);
}

/** 離地了沒。純視覺與統計用；**傷害能不能打到你是逐招比高度**，見 `hurtPlayer`。

    ⚠️ **比的是腳下的地面高度 `P.gy`，不是 0**。地圖那一輪加了可站立平台，
    寫死 0 的話「站在 6.5 高的高台上」會被判成飛行 —— 輸出打七折、
    近戰打不到你，高台變成無條件最優解，決定 6（高處不給安全）直接失效。 */
export function airborne() {
  return P.y > P.gy + 0.35;
}

/** 空中的輸出倍率寫進 `P.airMul`，由 `damageEnemy` 的入口乘上去 ——
    乘在入口，池子武裝、機體武裝、進化全部自動吃到，不用逐把接線
    （跟磁軌校準的增傷同一個位置）。 */
function syncAirMul() {
  P.airMul = airborne() ? (P.s.airDmg === undefined ? 0.7 : P.s.airDmg) : 1;
}

export function updateFlight(dt, inp) {
  /* 腳下的地面高度。**每一步只查一次**，之後所有人（`airborne`、HUD、
     受擊高度）都讀 `P.gy` —— 各自再查一次的話，玩家在斜坡上移動的那一幀
     會有人拿到舊值，症狀是走坡時輸出忽高忽低。 */
  var g = P.gy = groundYAt(P.pos.x, P.pos.z);
  var tMax = P.s.thrustMax || 100;
  var hold = !!inp.thrust && P.alive && !STATE.over && STATE.phase !== 'clear';
  /* **門檻只擋「從地面起飛」這一件事。** 一旦離地，放開再按、上上下下
     都只要還有一滴能量就行（使用者 2026-08-03 指定）——
     在空中被門檻擋住的話，玩家會在半空中突然失去控制權，那是最壞的手感。
     地面上則相反：能量沒到 65% 就是飛不起來，那是難度閥門。 */
  var airNow = P.flying || P.y > g + 0.05;
  var canFly = hold && P.thrust > 0 && (airNow || P.thrust >= takeoffCost());

  if (canFly) {
    if (!P.flying) { P.flying = true; P.apexY = P.y; SFX.dash(); }
    /* **越高燒得越快**：高度懲罰是二次的，所以「再上去一點」的代價
       成長得比玩家的直覺快 —— 那正是我們要的自我調節。 */
    var hk = 1 + Math.pow(Math.max(0, P.y - g) / FLY.softH, 2);
    P.thrust = Math.max(0, P.thrust - FLY.use * hk * dt);
    P.thrustCd = FLY.regenDelay;
    var top = g + FLY.maxH;
    P.vy = P.y < top ? (P.s.thrustRise || 8) : 0;
    if (P.y >= top) P.y = top;
  } else {
    P.flying = false;
    P.thrustCd -= dt;
    if (P.thrustCd <= 0 && P.thrust < tMax) {
      var rg = (P.s.thrustRegen || 40) * (P.y <= g + 0.05 ? FLY.groundRegenMul : 1);
      P.thrust = Math.min(tMax, P.thrust + rg * dt);
    }
    if (P.y > g) P.vy = Math.max(-FLY.fallMax, P.vy - FLY.fallAcc * dt);
  }

  P.y += P.vy * dt;
  if (P.y > g) { P.airT += dt; if (P.y > P.apexY) P.apexY = P.y; }
  if (P.y <= g) {
    var fell = P.apexY - g;
    var hard = P.vy < -8 && fell >= FLY.slamMinH;
    P.y = g; P.vy = 0;
    if (hard) slam();
    P.apexY = g;
  }
  syncAirMul();
}

/** 落地衝擊波。空中輸出被打折，**砸下來是把那些傷害拿回來的方式** ——
    範圍擊退加少量傷害，用途是解圍不是主力輸出。 */
function slam() {
  var r = FLY.slamR * (P.s.slamArea || 1) * (P.s.area || 1);
  areaDamage(P.pos.x, P.pos.z, r, P.s.slamDmg || 40, 0x9fd4ff, undefined, 'slam');
  spawnRing(P.pos.x, P.pos.z, 0x9fd4ff, 0.7, r * 1.6, 0.38);
  SFX.boom(); shake(0.42);
}
